import { toString } from 'lodash';
import { ONE_HUNDRED_VALUE } from 'common/config/app';
import { MAX_CHAR_LENGTH, NOT_APPLICABLE } from 'common/constants/general';
import { GRID_NUMBER_CHECKBOX } from 'common/constants/gridType';
import { largeCurrencyFormat } from 'common/formats/formats';
import { Cells } from 'common/types/scalarSpreadsheet';
import NumberCheckbox from 'components/ScalarSpreadsheet/components/number-multiple-checkbox/NumberMultipleCheckbox';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  FUND_INVESTMENTS_EMPTY_CELL,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ADJUSTMENT_SELECTION_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ALLOW_NEGATIVE_VALUE,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_CALCULATIONS_COLUMNS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMNS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COMPANY_TITLE_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_DATES_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_INDEX_ETF_COUNT,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_PUBLIC_COMPANY_ROWS_COUNT,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_INDEX_TITLE_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MARKET_ADJUSTMENT_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MEAN_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MEDIAN_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_SELECTION_A_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_SELECTION_B_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ROW_SPAN,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_SPECIFIED_ADJUSTMENT_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLE_ALIAS,
  FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_WEIGHTED_ALIAS,
  getFundInvestmentMarketAdjustmentColumn,
} from 'pages/FundInvestments/common/constants';
import { FundInvestmentMarketAdjustmentCellData } from 'pages/FundInvestments/hooks/useFundInvestmentMarketAdjustmentSheet';
import { getFormattedKeyLabel } from 'pages/FundInvestments/utils';
import { generateTotalExpression, getExpression, getNumberValue, getObjectValue } from 'utilities';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import { CellParserParams, CreateParserParams } from './createParser.types';
import {
  getWeightedExpression,
  handleAdjustmentSelectionCells,
  handleCompanyTitleCells,
  handleDateCells,
  handleMeanMedianCells,
  handlePercentileSelectionCells,
  handleWeightedCells,
  normalizeHiddenCell,
  normalizePercentageCell,
} from './utils';

const {
  ticker: TICKER,
  market_cap: MARKET_CAP,
  metric_selection_as_of_date: METRIC_SELECTION_AS_OF_DATE,
  metric_selection_as_of_market_date: METRIC_SELECTION_AS_OF_MARKET_DATE,
  percentage_change: PERCENTAGE_CHANGE,
  weighting: WEIGHTING,
} = FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COLUMNS;

const {
  metric_selection: METRIC_SELECTION,
  weighted: WEIGHTED,
  median: MEDIAN,
  mean: MEAN,
  percentile_selection_a: PERCENTILE_SELECTION_A,
  percentile_selection_b: PERCENTILE_SELECTION_B,
  specified_adjustment: SPECIFIED_ADJUSTMENT,
  adjustment_selection: ADJUSTMENT_SELECTION,
  market_adjustment: MARKET_ADJUSTMENT,
} = FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_CALCULATIONS_COLUMNS;

const cellParser = (params: CellParserParams): ICell<FundInvestmentMarketAdjustmentCellData> => {
  const {
    adjustmentOptions,
    allComparisonsExpression,
    allWeightingExpression,
    asOfDate,
    colNumber,
    column,
    columnLegend,
    forceReadOnly,
    isSingleComparison,
    key,
    marketAdjustment,
    marketAdjustmentOptionPairs,
    marketDate,
    metricAsOfDateColumnLegend,
    metricAsOfMarketDateColumnLegend,
    percentileSelection,
    row,
    rowNumber,
  } = params;

  const { alias, data } = getObjectValue(row);
  const { value: cellValue = '' } = getObjectValue(column[alias]);

  // Creating cell from row and column
  const cell: ICell<FundInvestmentMarketAdjustmentCellData> = { ...row, key, value: cellValue };

  const currentColumn = getFundInvestmentMarketAdjustmentColumn(colNumber);
  const { title } = getObjectValue(currentColumn);

  const { isIndex } = getObjectValue(data);

  const { percentile_selection_a: percentileSelectionA, percentile_selection_b: percentileSelectionB } =
    getObjectValue(percentileSelection);

  const {
    adjustment_selection: adjustmentSelection,
    metric_selection: metricSelection,
    specified_adjustment: specifiedAdjustment,
  } = getObjectValue(marketAdjustment);

  const metricAsOfDateCell = `${metricAsOfDateColumnLegend}${rowNumber}`;
  const metricAsOfMarketDateCell = `${metricAsOfMarketDateColumnLegend}${rowNumber}`;

  const percentageChangeExpression = `=(${metricAsOfMarketDateCell}/${metricAsOfDateCell}) - 1`;

  // Generate the cell based on the alias
  switch (alias) {
    // Generate title cells
    case FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_TITLE_ALIAS:
      cell.hidden = true;
      break;

    // Generate company title cells
    case FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_COMPANY_TITLE_ALIAS:
      handleCompanyTitleCells({
        cell,
        colNumber,
        metricSelection,
        title,
      });
      break;

    // Generate index title cells
    case FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_INDEX_TITLE_ALIAS:
      cell.value = title;
      break;

    // Generate date cells
    case FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_DATES_ALIAS:
      handleDateCells({
        asOfDate,
        cell,
        colNumber,
        marketDate,
      });
      break;

    // Generate weighted cells
    case FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_WEIGHTED_ALIAS:
      handleWeightedCells({
        allComparisonsExpression,
        allWeightingExpression,
        cell,
        colNumber,
      });
      break;

    // Generate median cells
    case FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MEDIAN_ALIAS:
      handleMeanMedianCells({
        allComparisonsExpression,
        cell,
        colNumber,
        formula: 'MEDIAN',
        key: MEDIAN.key,
        title: MEDIAN.title,
      });
      break;

    // Generate mean cells
    case FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MEAN_ALIAS:
      handleMeanMedianCells({
        allComparisonsExpression,
        cell,
        colNumber,
        formula: 'MEAN',
        key: MEAN.key,
        title: MEAN.title,
      });
      break;

    // Generate percentile selection A cells
    case FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_SELECTION_A_ALIAS:
      handlePercentileSelectionCells({
        allComparisonsExpression,
        cell,
        colNumber,
        key: PERCENTILE_SELECTION_A.key,
        optionKey: PERCENTILE_SELECTION_A.optionKey,
        percentileSelection: percentileSelectionA,
      });
      break;

    // Generate percentile selection B cells
    case FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_PERCENTILE_SELECTION_B_ALIAS:
      handlePercentileSelectionCells({
        allComparisonsExpression,
        cell,
        colNumber,
        key: PERCENTILE_SELECTION_B.key,
        optionKey: PERCENTILE_SELECTION_B.optionKey,
        percentileSelection: percentileSelectionB,
      });
      break;

    // Generate specified adjustment cells
    case FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_SPECIFIED_ADJUSTMENT_ALIAS:
      switch (colNumber) {
        case METRIC_SELECTION_AS_OF_MARKET_DATE.column:
          cell.hidden = false;
          cell.value = SPECIFIED_ADJUSTMENT.title;
          break;

        case PERCENTAGE_CHANGE.column:
          normalizePercentageCell(cell);

          cell.customKey = SPECIFIED_ADJUSTMENT.key;
          cell.dbType = 'string';
          cell.readOnly = false;
          cell.value = getNumberValue(specifiedAdjustment);
          break;

        default:
          normalizeHiddenCell(cell);
          break;
      }
      break;

    // Generate adjustment selection cells
    case FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ADJUSTMENT_SELECTION_ALIAS:
      handleAdjustmentSelectionCells({
        adjustmentOptions,
        adjustmentSelection,
        cell,
        colNumber,
        isSingleComparison,
        percentileSelectionA,
        percentileSelectionB,
      });
      break;

    // Generate market adjustment cells
    case FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_MARKET_ADJUSTMENT_ALIAS:
      switch (colNumber) {
        case METRIC_SELECTION_AS_OF_MARKET_DATE.column:
          cell.hidden = false;
          cell.value = MARKET_ADJUSTMENT.title;
          break;

        case PERCENTAGE_CHANGE.column:
          normalizePercentageCell(cell);

          cell.customKey = MARKET_ADJUSTMENT.key;
          cell.dbType = 'string';
          cell.expr = `=FROM_KEY_VALUE_PAIRS([${marketAdjustmentOptionPairs}], ${ADJUSTMENT_SELECTION.key})`;
          break;

        default:
          normalizeHiddenCell(cell);
          break;
      }
      break;

    default:
      // Generate column cells
      switch (colNumber) {
        case TICKER.column:
          cell.value = toString(cellValue);
          break;

        case MARKET_CAP.column:
          cell.format = largeCurrencyFormat;
          cell.gridType = 'number';
          cell.value = getNumberValue(cellValue as number);
          break;

        case METRIC_SELECTION_AS_OF_DATE.column:
        case METRIC_SELECTION_AS_OF_MARKET_DATE.column:
          cell.expr = isIndex ? null : `=FROM_KEY(${toString(cellValue)}, ${METRIC_SELECTION.key})`;
          cell.format = largeCurrencyFormat;
          cell.gridType = 'number';
          cell.value = isIndex ? getNumberValue(cellValue as number) : null;
          break;

        case PERCENTAGE_CHANGE.column:
          normalizePercentageCell(cell);

          cell.component = <NumberCheckbox cell={FUND_INVESTMENTS_EMPTY_CELL} rightAlignContent />;
          cell.data = {
            ...getObjectValue(cell.data),
            isInvalidForCheckbox: !Number.isFinite(Number(cellValue)) || cellValue === NOT_APPLICABLE || Number(cellValue) <= 0,
          };
          cell.enabled = Boolean(cellValue);
          cell.expr = percentageChangeExpression;
          cell.forceComponent = true;
          cell.gridType = GRID_NUMBER_CHECKBOX;
          cell.readOnly = false;
          break;

        case WEIGHTING.column:
          normalizePercentageCell(cell);

          cell.allowNegativeValue = false;
          cell.data = { ...getObjectValue(cell.data), isWeighting: true };
          cell.dbType = 'string';
          cell.maxValue = ONE_HUNDRED_VALUE;
          cell.minValue = 0;
          cell.readOnly = false;
          cell.value = getNumberValue(cellValue as number);
          break;

        default:
          break;
      }
      break;
  }

  // Default values
  const {
    allowEmptyValues = true,
    allowNegativeValue = FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ALLOW_NEGATIVE_VALUE,
    customKey = null,
    dbType = 'string',
    expr = '',
    format = null,
    gridType = 'string',
    maxNumberChars = MAX_CHAR_LENGTH,
    readOnly = true,
    rowSpan = FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_ROW_SPAN,
    tooltipMessages = [],
    value = '',
  } = getObjectValue(cell);

  return {
    ...cell,
    allowEmptyValues,
    allowNegativeValue,
    columnLegend,
    customKey,
    dbType,
    expr: getExpression({ expr, columnLegend }),
    format,
    gridType,
    key,
    maxNumberChars,
    readOnly: forceReadOnly || readOnly,
    rowSpan,
    tooltipMessages,
    value,
  } as ICell<FundInvestmentMarketAdjustmentCellData>;
};

const createParser = (params: CreateParserParams): Cells => {
  const {
    adjustmentOptions,
    asOfDate,
    columns,
    companyTypesPresence,
    forceReadOnly,
    isSingleComparison,
    marketAdjustment,
    marketDate,
    percentileSelection,
    rowConfig,
  } = params;

  const { hasPublicCompany, indexEtfCount, publicCompaniesCount } = companyTypesPresence;

  const alphabet = alphabetGenerator([], columns.length);
  const cells = {} as Cells<FundInvestmentMarketAdjustmentCellData>;

  // Column legends
  const metricAsOfDateColumnLegend = alphabet[METRIC_SELECTION_AS_OF_DATE.column - 1];
  const metricAsOfMarketDateColumnLegend = alphabet[METRIC_SELECTION_AS_OF_MARKET_DATE.column - 1];
  const weightingColumnLegend = alphabet[WEIGHTING.column - 1];

  // All comparisons (public companies and index ETFs) expression
  const publicCompanyRows = Array.from(
    { length: publicCompaniesCount },
    (_, index) => FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_PUBLIC_COMPANY_ROWS_COUNT + index + 1 // +1 for 1-based indexing
  );

  const indexEtfStartRow = hasPublicCompany
    ? getNumberValue(publicCompanyRows.at(-1)) +
      FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_INDEX_ETF_COUNT +
      1 // +1 for 1-based indexing
    : FUND_INVESTMENTS_MARKET_ADJUSTMENT_SPREADSHEET_EXCLUDED_PUBLIC_COMPANY_ROWS_COUNT + 1; // +1 for 1-based indexing;

  const indexEtfRows = Array.from({ length: indexEtfCount }, (_, index) => indexEtfStartRow + index);

  const allComparisonsRows = [...publicCompanyRows, ...indexEtfRows];

  const allComparisonsExpression = generateTotalExpression({
    items: allComparisonsRows,
    generateIndex: false,
  });

  const allWeightingExpression = getExpression({
    expr: allComparisonsExpression.join(', '),
    columnLegend: weightingColumnLegend,
  });

  const marketAdjustmentOptions = {
    Specified: SPECIFIED_ADJUSTMENT.key,
    Weighted: isSingleComparison
      ? getWeightedExpression({
        allComparisonsExpression,
        allWeightingExpression,
        isSingleComparison,
      })
      : WEIGHTED.key,
  };

  // Add mean, median and percentiles only when there is more than one comparison
  if (!isSingleComparison) {
    Object.assign(marketAdjustmentOptions, {
      '25th Percentile': `PERCENTILE(FILTER_ENABLED([${allComparisonsExpression}]), 25)`,
      '75th Percentile': `PERCENTILE(FILTER_ENABLED([${allComparisonsExpression}]), 75)`,
      [`GET_PERCENTILE_OPTION(${PERCENTILE_SELECTION_A.optionKey})`]: PERCENTILE_SELECTION_A.key,
      [`GET_PERCENTILE_OPTION(${PERCENTILE_SELECTION_B.optionKey})`]: PERCENTILE_SELECTION_B.key,
      Mean: MEAN.key,
      Median: MEDIAN.key,
    });
  }

  const marketAdjustmentOptionPairs = Object.entries(marketAdjustmentOptions)
    .map(([label, value]) => `[${getFormattedKeyLabel(label)}, ${value}]`)
    .join(',');

  rowConfig.forEach((row, rowIndex: number) => {
    columns.forEach((column, colIndex: number) => {
      const rowNumber = rowIndex + 1;
      const colNumber = colIndex + 1;
      const columnLegend = alphabet[colIndex];

      const key = columnLegend + rowNumber;

      cells[key] = cellParser({
        adjustmentOptions,
        allComparisonsExpression,
        allWeightingExpression,
        asOfDate,
        colNumber,
        column,
        columnLegend,
        forceReadOnly,
        isSingleComparison,
        key,
        marketAdjustment,
        marketAdjustmentOptionPairs,
        marketDate,
        metricAsOfDateColumnLegend,
        metricAsOfMarketDateColumnLegend,
        percentileSelection,
        row,
        rowNumber,
      });
    });
  });

  return cells;
};

export default createParser;
